/**
 * TSG AI Access Utility
 * 
 * TSG AI (https://www.tsg-ai.com) is available for users with:
 * - ₹7999 subscription (3 months)
 * - ₹14999 subscription (6 months)
 */

import API_BASE_URL from '../Config/config';

/**
 * Check if user has access to TSG AI
 * Returns true if user has ₹7999 or ₹14999 active subscription
 */
export const hasTsgAiAccess = async (userId) => {
  if (!userId) {
    return {
      hasAccess: false,
      subscription: null
    };
  }

  try {
    // Check database for active subscription with 7999 or 14999 amount, OR LLM trial
    const response = await fetch(`${API_BASE_URL}/api/skillgenie-premium/status/${userId}`, {
      signal: AbortSignal.timeout(5000)
    });

    if (response.ok) {
      const data = await response.json();
      
      if (data.ok && data.hasActiveSubscription && data.subscription) {
        const subscription = data.subscription;
        const amount = Number(subscription.amount);
        
        // Check if subscription is valid and:
        // 1. Amount is 7999 or 14999 (full premium plans), OR
        // 2. It's a Java or Python Premium plan
        // NOTE: Free trials (llm_trial) NO LONGER grant TSG AI access as per recent requirements
        if (subscription.end_date) {
          const endDate = new Date(subscription.end_date);
          const now = new Date();
          
          if (endDate > now && (
            (amount >= 2000) || 
            (subscription.plan_name.toLowerCase().includes('premium')) ||
            (subscription.plan_name.toLowerCase().includes('elite')) ||
            (subscription.plan_name.toLowerCase().includes('java')) || 
            (subscription.plan_name.toLowerCase().includes('python'))
          )) {
            return {
              hasAccess: true,
              subscription: {
                amount: amount,
                planName: subscription.plan_name,
                endDate: subscription.end_date,
                daysRemaining: subscription.daysRemaining,
                isTrial: false // Trials don't get here anymore
              }
            };
          }
        }
      }
    }
    
    // LLM trial check removed for TSG AI access as it should be restricted
  } catch (error) {
    console.warn('⚠️ TSG AI access check failed:', error.message);
    
    // Fallback: Check localStorage
    try {
      const paymentKey = `payment_confirmed_${userId}`;
      const storedPayment = localStorage.getItem(paymentKey);
      
      if (storedPayment) {
        const payment = JSON.parse(storedPayment);
        const amount = Number(payment.amount);
        
        if (payment.expiryDate) {
          const expiryDate = new Date(payment.expiryDate);
          const now = new Date();
          
          if (expiryDate > now && (
            amount >= 2000 || 
            payment.planName.toLowerCase().includes('premium') || 
            payment.planName.toLowerCase().includes('elite') ||
            payment.planName.toLowerCase().includes('java') || 
            payment.planName.toLowerCase().includes('python')
          )) {
            return {
              hasAccess: true,
              subscription: {
                amount: amount,
                planName: payment.planName,
                endDate: payment.expiryDate
              }
            };
          }
        }
      }
    } catch (e) {
      console.error('Error checking localStorage for TSG AI access:', e);
    }
  }

  return {
    hasAccess: false,
    subscription: null
  };
};

/**
 * Get TSG AI URL
 */
export const TSG_AI_URL = 'https://www.tsg-ai.com';

const tsgAiAccess = {
  hasTsgAiAccess,
  TSG_AI_URL
};

export default tsgAiAccess;




